/**
 * Flow state -> `POST /v2/onboarding/complete` request body.
 *
 * The two shapes are close but not identical, and the backend schema is
 * `.unknown(false)` — a single stray key fails the whole request with a 400,
 * so this cannot be "just send the state object".
 *
 * Three differences, all of them deliberate rather than accidental drift:
 *
 *   `dob` dropped. Date of birth was saved at registration; completion reads it
 *   from the account rather than accepting another copy.
 *
 *   `email` / `emailVerified` dropped. The server knows both already; the
 *   session doing the call *is* the verified account. Echoing them back would
 *   let a client claim a verification it did not earn.
 *
 *   `consents` dropped. Receipts are written in the registration transaction
 *   against `(type, revision)`. Re-sending them at completion would either be
 *   ignored or create a second, contradictory receipt.
 *
 * Everything else — goals, goalData, profile, demographics — passes through
 * untouched. The vocabularies on both sides are generated from the same
 * taxonomy (31 sectors, 52 sub-types, 5 goals, 4 demographic dimensions) and
 * match exactly, so there is nothing to translate.
 */

/**
 * @param {object} state flow state from `FlowProvider`
 * @returns {object} the request body
 */
export const buildCompletionPayload = (state) => {
  const payload = {
    pathway: state.pathway,
    goals: state.goals ?? [],
    goalData: state.goalData ?? {},
    profile: buildProfile(state.profile ?? {}),
  };

  // `demographics` is optional server-side and defaults to {}. Sending an empty
  // object is valid, but omitting it keeps the request honest about the member
  // having skipped an optional step.
  const demographics = pruneEmpty(state.demographics ?? {});
  if (Object.keys(demographics).length) payload.demographics = demographics;

  return payload;
};

/**
 * Only the fields the API accepts, and only when they carry a value.
 *
 * `address` is the exception: it is *required* and explicitly nullable, so a
 * member who skipped it must send `null` rather than omitting the key.
 */
const buildProfile = (profile) => {
  const out = {
    firstName: profile.firstName,
    lastName: profile.lastName,
    mainPhoto: profile.mainPhoto,
    address: profile.address ?? null,
  };

  if (profile.currentRole?.title && profile.currentRole?.organization) {
    out.currentRole = profile.currentRole;
  }
  if (profile.pastRoles?.length) out.pastRoles = profile.pastRoles;
  if (profile.biography) out.biography = profile.biography;
  if (profile.languages?.length) out.languages = profile.languages;

  // The UI keeps three fixed slots so the layout does not reflow as photos are
  // added; unfilled slots are holes in the array, not photos.
  const extra = (profile.additionalPhotos ?? []).filter(Boolean);
  if (extra.length) out.additionalPhotos = extra;

  return out;
};

/** Drop empty selections so an untouched dimension is absent, not `[]`. */
const pruneEmpty = (demographics) =>
  Object.fromEntries(
    Object.entries(demographics).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === "object")
        return Object.keys(value).length > 0;
      return value !== null && value !== undefined && value !== "";
    }),
  );

/**
 * Flow-state consents -> the `consents` block on `POST /v2/auth/register`.
 *
 * The reducer stores `{ version, acceptedAt }` per policy; the API wants
 * `{ revision }`. `acceptedAt` is the client's clock and is deliberately not
 * sent — the server timestamps the receipt itself.
 */
export const buildConsentAcknowledgements = (consents = {}, policyTypes) =>
  Object.fromEntries(
    policyTypes.map((type) => [type, { revision: consents[type]?.version }]),
  );
